import { useMemo, useState, type RefObject } from "react"
import { ArrowDown, ArrowUp, ChevronDown, Copy, LayoutPanelLeft, Loader2, Trash2 } from "lucide-react"

import type { ReportLayoutWidgetDto, ReportTemplateDatasourceDto, ReportTemplateFlowSectionDto } from "../../api/reportTemplateService"
import { Badge } from "shared/ui/badge"
import { Button } from "shared/ui/button"
import { Card, CardContent, CardHeader, CardTitle } from "shared/ui/card"
import { Checkbox } from "shared/ui/checkbox"
import { Input } from "shared/ui/input"
import { Label } from "shared/ui/label"
import { Popover, PopoverContent, PopoverTrigger } from "shared/ui/popover"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "shared/ui/select"
import { Textarea } from "shared/ui/textarea"
import {
  BORDER_SIDES,
  MIN_WIDGET_HEIGHT,
  MIN_WIDGET_WIDTH,
  WORKER_TABLE_COLUMNS,
  clamp,
  getWidgetFlowOrder,
  getWidgetPageBreakBefore,
  getWidgetManualPage,
  getWidgetImageFit,
  getWidgetLocked,
  getWidgetPositionMode,
  type BorderLineStyle,
  type BorderSide,
  type HorizontalAlign,
  type ImageFitMode,
  type TableColumnWidthMap,
  type TableSourceMode,
  type VerticalAlign,
} from "./editorUtils"

export interface WidgetPropertiesCardProps {
  layoutSection: "body" | "header" | "footer"
  manualPageCount: number
  flowSections: ReportTemplateFlowSectionDto[]
  selectedWidget: ReportLayoutWidgetDto | null
  canvasMetrics: { width: number; height: number }
  selectedWidgetBorder: {
    borderStyle: BorderLineStyle
    borderWidth: number
    borderColor: string
    sides: Record<BorderSide, boolean>
  } | null
  selectedWidgetCornerRadius: number
  selectedWidgetTextAlign: HorizontalAlign
  selectedWidgetVerticalAlign: VerticalAlign
  selectedWidgetTypography: {
    fontSize: number
    fontWeight: "normal" | "bold"
    fontStyle: "normal" | "italic"
  }
  fieldOptions: Array<{ key: string; label: string }>
  datasources: ReportTemplateDatasourceDto[]
  fieldLabelMap: Map<string, string>
  tableSource: TableSourceMode
  tableDatasourceId: string
  tableColumnOptions: Array<{ key: string; label: string }>
  tableSelectedColumns: string[]
  tableColumnWidths: TableColumnWidthMap
  uploadingImageWidgetId: string | null
  imageFileInputRef: RefObject<HTMLInputElement | null>
  updateWidget: (widgetId: string, patch: Partial<ReportLayoutWidgetDto>) => void
  updateWidgetProps: (widgetId: string, patch: Record<string, unknown>) => void
  setWidgetBorderSide: (widgetId: string, side: BorderSide, checked: boolean) => void
  setWidgetBorderAll: (widgetId: string, checked: boolean) => void
  toggleTableColumn: (widgetId: string, fieldKey: string, checked: boolean) => void
  moveTableColumn: (widgetId: string, fieldKey: string, direction: "up" | "down") => void
  setTableColumnWidth: (widgetId: string, fieldKey: string, widthPercent: number | null) => void
  handleImageFileSelected: (widgetId: string, file?: File | null) => Promise<void>
  bringWidgetToFront: (widgetId: string) => void
  duplicateWidget: (widgetId: string) => void
  removeWidget: (widgetId: string) => void
}

interface SearchableFieldKeySelectProps {
  value: string
  options: Array<{ key: string; label: string }>
  placeholder: string
  onChange: (value: string) => void
}

function SearchableFieldKeySelect({
  value,
  options,
  placeholder,
  onChange,
}: SearchableFieldKeySelectProps) {
  const [open, setOpen] = useState(false)
  const [search, setSearch] = useState("")

  const selectedOption = useMemo(
    () => options.find((item) => item.key === value) ?? null,
    [options, value],
  )

  const filteredOptions = useMemo(() => {
    const keyword = search.trim().toLowerCase()
    if (!keyword) return options
    return options.filter((item) => {
      const aliases = item.key === "permit_number" ? "permit no permit number" : ""
      const haystack = `${item.label} ${item.key} ${item.key.replace(/_/g, " ")} ${aliases}`.toLowerCase()
      return haystack.includes(keyword)
    })
  }, [options, search])

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button type="button" variant="outline" className="w-full justify-between font-normal">
          <span className="truncate text-left">
            {selectedOption ? `${selectedOption.label} (${selectedOption.key})` : placeholder}
          </span>
          <ChevronDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[420px] space-y-2 p-3" align="start">
        <Input
          value={search}
          onChange={(event) => setSearch(event.target.value)}
          placeholder="Search by label or field key"
        />
        <div className="max-h-64 space-y-1 overflow-y-auto pr-1">
          <Button
            type="button"
            variant={!value ? "default" : "outline"}
            className="w-full justify-start"
            onClick={() => {
              onChange("")
              setOpen(false)
              setSearch("")
            }}
          >
            -
          </Button>
          {filteredOptions.length > 0 ? (
            filteredOptions.map((item) => (
              <Button
                key={item.key}
                type="button"
                variant={item.key === value ? "default" : "outline"}
                className="h-auto w-full justify-start py-2 text-left"
                onClick={() => {
                  onChange(item.key)
                  setOpen(false)
                  setSearch("")
                }}
              >
                <span className="truncate">{item.label}</span>
                <span className="ml-2 truncate text-xs opacity-70">{item.key}</span>
              </Button>
            ))
          ) : (
            <div className="py-3 text-center text-sm text-gray-500">No fields found</div>
          )}
        </div>
      </PopoverContent>
    </Popover>
  )
}

export function WidgetPropertiesCard({
  layoutSection,
  manualPageCount,
  flowSections,
  selectedWidget,
  canvasMetrics,
  selectedWidgetBorder,
  selectedWidgetCornerRadius,
  selectedWidgetTextAlign,
  selectedWidgetVerticalAlign,
  selectedWidgetTypography,
  fieldOptions,
  datasources,
  fieldLabelMap,
  tableSource,
  tableDatasourceId,
  tableColumnOptions,
  tableSelectedColumns,
  tableColumnWidths,
  uploadingImageWidgetId,
  imageFileInputRef,
  updateWidget,
  updateWidgetProps,
  setWidgetBorderSide,
  setWidgetBorderAll,
  toggleTableColumn,
  moveTableColumn,
  setTableColumnWidth,
  handleImageFileSelected,
  bringWidgetToFront,
  duplicateWidget,
  removeWidget,
}: WidgetPropertiesCardProps) {
  const selectedPositionMode = selectedWidget ? getWidgetPositionMode(selectedWidget) : "flow"
  const selectedFlowOrder = selectedWidget ? getWidgetFlowOrder(selectedWidget, 1) : 1
  const selectedManualPage = selectedWidget ? getWidgetManualPage(selectedWidget, 1) : 1
  const selectedPageBreakBefore = selectedWidget ? getWidgetPageBreakBefore(selectedWidget) : false
  const selectedWidgetLocked = selectedWidget ? getWidgetLocked(selectedWidget) : false
  const selectedFlowSectionId = (() => {
    const current = String(selectedWidget?.props?.flowSectionId || "").trim()
    if (current && flowSections.some((section) => section.sectionId === current)) return current
    return flowSections[0]?.sectionId || "section-default"
  })()
  const widgetType = selectedWidget?.type ?? null
  const supportsBorderControls = widgetType !== "line"
  const supportsCornerRadius = widgetType !== "line"
  const supportsTypography = widgetType === "text" || widgetType === "field"
  const supportsTextAlignment = widgetType === "text" || widgetType === "field"
  const showAppearanceSection =
    (supportsBorderControls && Boolean(selectedWidgetBorder)) ||
    supportsCornerRadius ||
    supportsTypography ||
    supportsTextAlignment
  const recordDatasourceOptions = useMemo(
    () =>
      datasources
        .filter((item) => item.cardinality === "record")
        .map((item) => ({ key: item.id, label: item.name || item.objectName || item.id })),
    [datasources],
  )
  const tableDatasourceOptions = useMemo(
    () =>
      datasources
        .filter((item) => item.cardinality === "table")
        .map((item) => ({ key: item.id, label: item.name || item.objectName || item.id })),
    [datasources],
  )
  const selectedFieldBindingMode =
    selectedWidget?.type === "field" && String(selectedWidget.props?.datasourceId || "").trim()
      ? "datasource"
      : "template"
  const selectedFieldDatasourceId = selectedWidget?.type === "field"
    ? String(selectedWidget.props?.datasourceId || "")
    : ""
  const selectedFieldDatasource = datasources.find((item) => item.id === selectedFieldDatasourceId) ?? null
  const datasourceFieldOptions = selectedFieldDatasource?.fields?.map((item) => ({
    key: item.fieldKey,
    label: item.label || item.fieldKey,
  })) ?? []

  return (
    <Card className="self-start xl:sticky xl:top-4 xl:w-[360px] xl:min-w-[360px]">
      <CardHeader className="space-y-1 pb-3">
        <CardTitle className="text-base">Widget Properties</CardTitle>
        <p className="text-xs text-gray-500">Select a widget on canvas to edit only supported settings.</p>
        {widgetType ? <Badge variant="outline" className="w-fit">{widgetType}</Badge> : null}
      </CardHeader>
      <CardContent className="max-h-[calc(100vh-8rem)] overflow-y-auto overflow-x-hidden pr-1">
        {!selectedWidget ? (
          <p className="text-sm text-gray-500">No widget selected</p>
        ) : (
          <div className="space-y-4">
            <details open className="rounded-md border bg-white p-2">
              <summary className="cursor-pointer select-none text-sm font-medium text-gray-700">Position & Flow</summary>
              <div className="mt-2 space-y-3">
            <div className="flex items-center gap-2 rounded-md border px-3 py-2">
              <Checkbox
                id="widget-locked"
                checked={selectedWidgetLocked}
                onCheckedChange={(checked) => updateWidgetProps(selectedWidget.id, { locked: Boolean(checked) })}
              />
              <Label htmlFor="widget-locked" className="cursor-pointer">
                Lock widget position & size
              </Label>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label>X</Label>
                <Input
                  type="number"
                  min={0}
                  max={canvasMetrics.width}
                  value={Math.round(selectedWidget.x)}
                  disabled={selectedWidgetLocked}
                  onChange={(event) =>
                    updateWidget(selectedWidget.id, {
                      x: Number.parseInt(event.target.value || "0", 10) || 0,
                    })
                  }
                />
              </div>
              <div className="space-y-1.5">
                <Label>Y</Label>
                <Input
                  type="number"
                  min={0}
                  max={canvasMetrics.height}
                  value={Math.round(selectedWidget.y)}
                  disabled={selectedWidgetLocked}
                  onChange={(event) =>
                    updateWidget(selectedWidget.id, {
                      y: Number.parseInt(event.target.value || "0", 10) || 0,
                    })
                  }
                />
              </div>
              <div className="space-y-1.5">
                <Label>Width</Label>
                <Input
                  type="number"
                  min={MIN_WIDGET_WIDTH}
                  max={canvasMetrics.width}
                  value={Math.round(selectedWidget.width)}
                  disabled={selectedWidgetLocked}
                  onChange={(event) =>
                    updateWidget(selectedWidget.id, {
                      width: Number.parseInt(event.target.value || String(MIN_WIDGET_WIDTH), 10) || MIN_WIDGET_WIDTH,
                    })
                  }
                />
              </div>
              <div className="space-y-1.5">
                <Label>Height</Label>
                <Input
                  type="number"
                  min={MIN_WIDGET_HEIGHT}
                  max={canvasMetrics.height}
                  value={Math.round(selectedWidget.height)}
                  disabled={selectedWidgetLocked}
                  onChange={(event) =>
                    updateWidget(selectedWidget.id, {
                      height: Number.parseInt(event.target.value || String(MIN_WIDGET_HEIGHT), 10) || MIN_WIDGET_HEIGHT,
                    })
                  }
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label>Position Mode</Label>
                <Select
                  value={selectedPositionMode}
                  onValueChange={(value: "flow" | "absolute") => {
                    const patch: Record<string, unknown> = { positionMode: value }
                    if (value === "flow" && !Number.isFinite(Number(selectedWidget?.props?.flowOrder))) {
                      patch.flowOrder = selectedFlowOrder
                    }
                    updateWidgetProps(selectedWidget.id, patch)
                  }}
                  disabled={selectedWidgetLocked}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="flow">Flow</SelectItem>
                    <SelectItem value="absolute">Absolute</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              {selectedPositionMode === "flow" ? (
                <div className="space-y-1.5">
                  <Label>Flow Order</Label>
                  <Input
                    type="number"
                    min={1}
                    max={9999}
                    value={selectedFlowOrder}
                    disabled={selectedWidgetLocked}
                    onChange={(event) =>
                      updateWidgetProps(selectedWidget.id, {
                        flowOrder: clamp(Number.parseInt(event.target.value || "1", 10) || 1, 1, 9999),
                      })
                    }
                  />
                </div>
              ) : (
                <div />
              )}
            </div>

            {layoutSection === "body" ? (
              <div className="space-y-1.5">
                <Label>Manual Page</Label>
                <Input
                  type="number"
                  min={1}
                  max={manualPageCount}
                  value={selectedManualPage}
                  disabled={selectedWidgetLocked}
                  onChange={(event) =>
                    updateWidgetProps(selectedWidget.id, {
                      manualPage: clamp(Number.parseInt(event.target.value || "1", 10) || 1, 1, manualPageCount),
                    })
                  }
                />
              </div>
            ) : null}

            {layoutSection === "body" ? (
              <div className="space-y-1.5">
                <Label>Flow Section</Label>
                <Select
                  value={selectedFlowSectionId}
                  onValueChange={(value) => updateWidgetProps(selectedWidget.id, { flowSectionId: value })}
                  disabled={selectedWidgetLocked}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {flowSections.map((section) => (
                      <SelectItem key={section.sectionId} value={section.sectionId}>
                        {section.name} ({section.breakMode === "newPage" ? "New page" : "Flow"})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            ) : null}

            {layoutSection === "body" && selectedPositionMode === "flow" ? (
              <div className="flex items-center gap-2 rounded-md border px-3 py-2">
                <Checkbox
                  id="widget-page-break-before"
                  checked={selectedPageBreakBefore}
                  disabled={selectedWidgetLocked}
                  onCheckedChange={(checked) =>
                    updateWidgetProps(selectedWidget.id, { pageBreakBefore: Boolean(checked) })
                  }
                />
                <Label htmlFor="widget-page-break-before" className="cursor-pointer">
                  Start on new page (Page Break Before)
                </Label>
              </div>
            ) : null}
              </div>
            </details>

            {showAppearanceSection ? (
              <details open className="rounded-md border bg-white p-2">
                <summary className="cursor-pointer select-none text-sm font-medium text-gray-700">Appearance</summary>
                <div className="mt-2 space-y-3">
                  {supportsBorderControls && selectedWidgetBorder ? (
                    <div className="space-y-3 rounded-md border p-3">
                      <div className="space-y-1.5">
                        <Label>Border Preset</Label>
                        <div className="grid grid-cols-2 gap-2">
                          <Button
                            type="button"
                            size="sm"
                            variant={
                              !selectedWidgetBorder.sides.top &&
                              !selectedWidgetBorder.sides.right &&
                              !selectedWidgetBorder.sides.bottom &&
                              !selectedWidgetBorder.sides.left
                                ? "default"
                                : "outline"
                            }
                            onClick={() => setWidgetBorderAll(selectedWidget.id, false)}
                          >
                            None
                          </Button>
                          <Button
                            type="button"
                            size="sm"
                            variant={
                              selectedWidgetBorder.sides.top &&
                              selectedWidgetBorder.sides.right &&
                              selectedWidgetBorder.sides.bottom &&
                              selectedWidgetBorder.sides.left
                                ? "default"
                                : "outline"
                            }
                            onClick={() => setWidgetBorderAll(selectedWidget.id, true)}
                          >
                            All
                          </Button>
                        </div>
                      </div>

                      <div className="space-y-1.5">
                        <Label>Border Style</Label>
                        <Select
                          value={selectedWidgetBorder.borderStyle}
                          onValueChange={(value: BorderLineStyle) =>
                            updateWidgetProps(selectedWidget.id, { borderStyle: value })
                          }
                        >
                          <SelectTrigger className="h-9">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="solid">Solid</SelectItem>
                            <SelectItem value="dashed">Dashed</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>

                      <div className="grid grid-cols-2 gap-2">
                        {BORDER_SIDES.map((side) => {
                          const checked = selectedWidgetBorder.sides[side]
                          return (
                            <Button
                              key={`border-side-${side}`}
                              type="button"
                              size="sm"
                              variant={checked ? "default" : "outline"}
                              onClick={() => setWidgetBorderSide(selectedWidget.id, side, !checked)}
                            >
                              {side.charAt(0).toUpperCase() + side.slice(1)}
                            </Button>
                          )
                        })}
                      </div>

                      <div className="space-y-1.5">
                        <Label>Border Thickness</Label>
                        <Input
                          type="number"
                          min={1}
                          max={12}
                          value={selectedWidgetBorder.borderWidth}
                          onChange={(event) =>
                            updateWidgetProps(selectedWidget.id, {
                              borderWidth: clamp(Number.parseInt(event.target.value || "1", 10) || 1, 1, 12),
                            })
                          }
                        />
                      </div>
                    </div>
                  ) : null}

                  {supportsCornerRadius ? (
                    <div className="space-y-1.5 rounded-md border p-3">
                      <Label>Corner Radius</Label>
                      <Input
                        type="number"
                        min={0}
                        max={80}
                        value={selectedWidgetCornerRadius}
                        onChange={(event) =>
                          updateWidgetProps(selectedWidget.id, {
                            cornerRadius: clamp(Number.parseInt(event.target.value || "0", 10) || 0, 0, 80),
                          })
                        }
                      />
                    </div>
                  ) : null}

                  {supportsTypography ? (
                    <div className="space-y-3 rounded-md border p-3">
                      <div className="space-y-1.5">
                        <Label>Font Size</Label>
                        <Input
                          type="number"
                          min={8}
                          max={72}
                          value={selectedWidgetTypography.fontSize}
                          onChange={(event) =>
                            updateWidgetProps(selectedWidget.id, {
                              fontSize: clamp(Number.parseInt(event.target.value || "14", 10) || 14, 8, 72),
                            })
                          }
                        />
                      </div>

                      <div className="grid grid-cols-3 gap-2">
                        <Button
                          type="button"
                          size="sm"
                          variant={
                            selectedWidgetTypography.fontWeight === "normal" && selectedWidgetTypography.fontStyle === "normal"
                              ? "default"
                              : "outline"
                          }
                          onClick={() =>
                            updateWidgetProps(selectedWidget.id, {
                              fontWeight: "normal",
                              fontStyle: "normal",
                            })
                          }
                        >
                          Normal
                        </Button>
                        <Button
                          type="button"
                          size="sm"
                          variant={selectedWidgetTypography.fontWeight === "bold" ? "default" : "outline"}
                          onClick={() =>
                            updateWidgetProps(selectedWidget.id, {
                              fontWeight: selectedWidgetTypography.fontWeight === "bold" ? "normal" : "bold",
                            })
                          }
                        >
                          Bold
                        </Button>
                        <Button
                          type="button"
                          size="sm"
                          variant={selectedWidgetTypography.fontStyle === "italic" ? "default" : "outline"}
                          onClick={() =>
                            updateWidgetProps(selectedWidget.id, {
                              fontStyle: selectedWidgetTypography.fontStyle === "italic" ? "normal" : "italic",
                            })
                          }
                        >
                          Italic
                        </Button>
                      </div>
                    </div>
                  ) : null}

                  {supportsTextAlignment ? (
                    <div className="grid grid-cols-2 gap-3 rounded-md border p-3">
                      <div className="space-y-1.5">
                        <Label>Text Align</Label>
                        <Select
                          value={selectedWidgetTextAlign}
                          onValueChange={(value: HorizontalAlign) =>
                            updateWidgetProps(selectedWidget.id, { textAlign: value })
                          }
                        >
                          <SelectTrigger>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="left">Left</SelectItem>
                            <SelectItem value="center">Center</SelectItem>
                            <SelectItem value="right">Right</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>

                      <div className="space-y-1.5">
                        <Label>Vertical Align</Label>
                        <Select
                          value={selectedWidgetVerticalAlign}
                          onValueChange={(value: VerticalAlign) =>
                            updateWidgetProps(selectedWidget.id, { verticalAlign: value })
                          }
                        >
                          <SelectTrigger>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="top">Top</SelectItem>
                            <SelectItem value="center">Center</SelectItem>
                            <SelectItem value="bottom">Bottom</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                  ) : null}
                </div>
              </details>
            ) : null}

            <details open className="rounded-md border bg-white p-2">
              <summary className="cursor-pointer select-none text-sm font-medium text-gray-700">Content</summary>
              <div className="mt-2 space-y-3">
                {selectedWidget.type === "text" ? (
              <div className="space-y-1.5">
                <Label>Text</Label>
                <Textarea
                  rows={4}
                  value={String(selectedWidget.props?.text || "")}
                  onChange={(event) =>
                    updateWidgetProps(selectedWidget.id, { text: event.target.value })
                  }
                />
              </div>
            ) : null}

            {selectedWidget.type === "field" ? (
              <div className="space-y-3">
                <div className="space-y-1.5">
                  <Label>Label</Label>
                  <Input
                    value={String(selectedWidget.props?.label || "")}
                    onChange={(event) =>
                      updateWidgetProps(selectedWidget.id, { label: event.target.value })
                    }
                  />
                </div>
                <div className="space-y-1.5">
                  <Label>Binding</Label>
                  <Select
                    value={selectedFieldBindingMode}
                    onValueChange={(value: "template" | "datasource") => {
                      if (value === "datasource") {
                        updateWidgetProps(selectedWidget.id, {
                          datasourceId: recordDatasourceOptions[0]?.key || "",
                          fieldKey: "",
                        })
                        return
                      }

                      updateWidgetProps(selectedWidget.id, {
                        datasourceId: "",
                        fieldKey: fieldOptions[0]?.key || "",
                      })
                    }}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="template">Template Field</SelectItem>
                      <SelectItem value="datasource">Report Data Source</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                {selectedFieldBindingMode === "datasource" ? (
                  <>
                    <div className="space-y-1.5">
                      <Label>Data Source</Label>
                      <Select
                        value={selectedFieldDatasourceId}
                        onValueChange={(value) =>
                          updateWidgetProps(selectedWidget.id, {
                            datasourceId: value,
                            fieldKey: datasources.find((item) => item.id === value)?.fields?.[0]?.fieldKey || "",
                          })
                        }
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Select record datasource" />
                        </SelectTrigger>
                        <SelectContent>
                          {recordDatasourceOptions.map((item) => (
                            <SelectItem key={item.key} value={item.key}>
                              {item.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-1.5">
                      <Label>Field Key</Label>
                      {datasourceFieldOptions.length > 0 ? (
                        <SearchableFieldKeySelect
                          value={String(selectedWidget.props?.fieldKey || "")}
                          options={datasourceFieldOptions}
                          placeholder="Select datasource field"
                          onChange={(value) => updateWidgetProps(selectedWidget.id, { fieldKey: value })}
                        />
                      ) : (
                        <Input
                          value={String(selectedWidget.props?.fieldKey || "")}
                          onChange={(event) =>
                            updateWidgetProps(selectedWidget.id, { fieldKey: event.target.value })
                          }
                        />
                      )}
                    </div>
                  </>
                ) : (
                  <div className="space-y-1.5">
                    <Label>Field Key</Label>
                    {fieldOptions.length > 0 ? (
                      <SearchableFieldKeySelect
                        value={String(selectedWidget.props?.fieldKey || "")}
                        options={fieldOptions}
                        placeholder="Select field key"
                        onChange={(value) => updateWidgetProps(selectedWidget.id, { fieldKey: value })}
                      />
                    ) : (
                      <Input
                        value={String(selectedWidget.props?.fieldKey || "")}
                        onChange={(event) =>
                          updateWidgetProps(selectedWidget.id, { fieldKey: event.target.value })
                        }
                      />
                    )}
                  </div>
                )}
                {selectedFieldBindingMode === "datasource" ? (
                  <p className="text-xs text-slate-500">
                    Bind field widgets to datasources with `Record` cardinality.
                  </p>
                ) : null}
              </div>
            ) : null}

            {selectedWidget.type === "image" ? (
              <div className="space-y-3">
                <div className="space-y-1.5">
                  <Label>Binding Type</Label>
                  <Select
                    value={String(selectedWidget.props?.bindingType || "static")}
                    onValueChange={(value) =>
                      updateWidgetProps(selectedWidget.id, { bindingType: value })
                    }
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="static">Static URL</SelectItem>
                      <SelectItem value="field">Field Binding</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1.5">
                  <Label>Image Fit</Label>
                  <Select
                    value={getWidgetImageFit(selectedWidget)}
                    onValueChange={(value: ImageFitMode) =>
                      updateWidgetProps(selectedWidget.id, { imageFit: value })
                    }
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="contain">Contain (Fit in box)</SelectItem>
                      <SelectItem value="cover">Cover (Crop to fill)</SelectItem>
                      <SelectItem value="fill">Fill (Stretch)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                {String(selectedWidget.props?.bindingType || "static") === "field" ? (
                  <div className="space-y-1.5">
                    <Label>Field Key</Label>
                    {fieldOptions.length > 0 ? (
                      <SearchableFieldKeySelect
                        value={String(selectedWidget.props?.fieldKey || "")}
                        options={fieldOptions}
                        placeholder="Select image field key"
                        onChange={(value) => updateWidgetProps(selectedWidget.id, { fieldKey: value })}
                      />
                    ) : (
                      <Input
                        value={String(selectedWidget.props?.fieldKey || "")}
                        onChange={(event) =>
                          updateWidgetProps(selectedWidget.id, { fieldKey: event.target.value })
                        }
                      />
                    )}
                  </div>
                ) : (
                  <div className="space-y-2">
                    <Label>Static URL</Label>
                    <Input
                      value={String(selectedWidget.props?.staticUrl || "")}
                      onChange={(event) =>
                        updateWidgetProps(selectedWidget.id, { staticUrl: event.target.value })
                      }
                      placeholder="https://... or /attachments/..."
                    />
                    <div className="flex items-center gap-2">
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        disabled={uploadingImageWidgetId === selectedWidget.id}
                        onClick={() => imageFileInputRef.current?.click()}
                      >
                        {uploadingImageWidgetId === selectedWidget.id ? (
                          <>
                            <Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" />
                            Uploading...
                          </>
                        ) : (
                          "Browse Image"
                        )}
                      </Button>
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        disabled={uploadingImageWidgetId === selectedWidget.id}
                        onClick={() => updateWidgetProps(selectedWidget.id, { staticUrl: "" })}
                      >
                        Clear
                      </Button>
                    </div>
                    <Input
                      ref={imageFileInputRef}
                      type="file"
                      accept="image/*"
                      className="hidden"
                      onChange={(event) => {
                        const file = event.target.files?.[0] ?? null
                        handleImageFileSelected(selectedWidget.id, file)
                        event.target.value = ""
                      }}
                    />
                    <p className="text-xs text-gray-500">
                      Tip: Browse will upload the image through the API attachment endpoint and store the URL in this layout
                    </p>
                  </div>
                )}
              </div>
            ) : null}

            {selectedWidget.type === "table" ? (
              <div className="space-y-2">
                <Label>Data Source</Label>
                <Select
                  value={tableSource}
                  onValueChange={(value: TableSourceMode) => {
                    const fallbackColumns =
                      value === "workers"
                        ? WORKER_TABLE_COLUMNS.slice(0, 4).map((item) => item.key).join(",")
                        : value === "datasource"
                          ? ""
                        : ""
                    updateWidgetProps(selectedWidget.id, {
                      tableSource: value,
                      datasourceId: value === "datasource" ? tableDatasourceOptions[0]?.key || "" : "",
                      columns: fallbackColumns,
                      columnWidths: {},
                    })
                  }}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="reportRows">Report Rows</SelectItem>
                    <SelectItem value="workers">Workers (Permit Detail)</SelectItem>
                    <SelectItem value="datasource">Report Data Source</SelectItem>
                  </SelectContent>
                </Select>

                {tableSource === "datasource" ? (
                  <div className="space-y-1.5">
                    <Label>Datasource</Label>
                    <Select
                      value={tableDatasourceId}
                      onValueChange={(value) =>
                        updateWidgetProps(selectedWidget.id, {
                          datasourceId: value,
                          columns: "",
                          columnWidths: {},
                        })
                      }
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select table datasource" />
                      </SelectTrigger>
                      <SelectContent>
                        {tableDatasourceOptions.map((item) => (
                          <SelectItem key={item.key} value={item.key}>
                            {item.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                ) : null}

                <Label>Columns</Label>
                {tableColumnOptions.length > 0 ? (
                  <div className="max-h-40 space-y-2 overflow-auto rounded-md border p-3">
                    {tableColumnOptions.map((item) => {
                      const fieldKey = item.key
                      const checked = tableSelectedColumns.includes(fieldKey)
                      return (
                        <div key={fieldKey} className="flex items-center gap-2">
                          <Checkbox
                            id={`table-column-${fieldKey}`}
                            checked={checked}
                            onCheckedChange={(value) =>
                              toggleTableColumn(selectedWidget.id, fieldKey, Boolean(value))
                            }
                          />
                          <Label htmlFor={`table-column-${fieldKey}`} className="text-sm font-normal">
                            {item.label || fieldLabelMap.get(fieldKey) || fieldKey}
                          </Label>
                        </div>
                      )
                    })}
                  </div>
                ) : (
                  <Textarea
                    rows={3}
                    value={String(selectedWidget.props?.columns || "")}
                    onChange={(event) =>
                      updateWidgetProps(selectedWidget.id, { columns: event.target.value })
                    }
                  />
                )}
                <p className="text-xs text-gray-500">
                  Selected: {tableSelectedColumns.length > 0 ? tableSelectedColumns.join(", ") : "-"}
                </p>
                {tableSource === "datasource" ? (
                  <p className="text-xs text-slate-500">
                    Bind table widgets to datasources with `Table` cardinality.
                  </p>
                ) : null}
                {tableSelectedColumns.length > 0 ? (
                  <div className="space-y-2 rounded-md border p-3">
                    <Label className="text-xs text-gray-500">Column Order</Label>
                    {tableSelectedColumns.map((fieldKey, index) => {
                      const columnLabel = tableColumnOptions.find((item) => item.key === fieldKey)?.label || fieldLabelMap.get(fieldKey) || fieldKey
                      const isFirst = index === 0
                      const isLast = index === tableSelectedColumns.length - 1
                      return (
                        <div key={`order-${fieldKey}-${index}`} className="flex items-center justify-between gap-2 rounded border p-2">
                          <span className="truncate text-sm">{columnLabel}</span>
                          <div className="inline-flex items-center gap-1">
                            <Button
                              type="button"
                              size="sm"
                              variant="outline"
                              disabled={isFirst}
                              onClick={() => moveTableColumn(selectedWidget.id, fieldKey, "up")}
                            >
                              <ArrowUp className="h-3.5 w-3.5" />
                            </Button>
                            <Button
                              type="button"
                              size="sm"
                              variant="outline"
                              disabled={isLast}
                              onClick={() => moveTableColumn(selectedWidget.id, fieldKey, "down")}
                            >
                              <ArrowDown className="h-3.5 w-3.5" />
                            </Button>
                          </div>
                        </div>
                      )
                    })}
                  </div>
                ) : null}
                {tableSelectedColumns.length > 0 ? (
                  <div className="space-y-2 rounded-md border p-3">
                    <Label className="text-xs text-gray-500">Column Width (%)</Label>
                    {tableSelectedColumns.map((fieldKey) => {
                      const columnLabel = tableColumnOptions.find((item) => item.key === fieldKey)?.label || fieldLabelMap.get(fieldKey) || fieldKey
                      const widthValue = tableColumnWidths[fieldKey]
                      return (
                        <div key={`width-${fieldKey}`} className="grid grid-cols-[1fr_100px_auto] items-center gap-2">
                          <span className="truncate text-sm">{columnLabel}</span>
                          <Input
                            type="number"
                            min={1}
                            max={100}
                            step={0.1}
                            value={Number.isFinite(widthValue) ? widthValue : ""}
                            placeholder="auto"
                            onChange={(event) => {
                              const raw = event.target.value.trim()
                              if (!raw) {
                                setTableColumnWidth(selectedWidget.id, fieldKey, null)
                                return
                              }
                              const parsed = Number(raw)
                              if (!Number.isFinite(parsed)) return
                              setTableColumnWidth(selectedWidget.id, fieldKey, clamp(parsed, 1, 100))
                            }}
                          />
                          <Button
                            type="button"
                            size="sm"
                            variant="outline"
                            onClick={() => setTableColumnWidth(selectedWidget.id, fieldKey, null)}
                          >
                            Auto
                          </Button>
                        </div>
                      )
                    })}
                  </div>
                ) : null}
              </div>
            ) : null}

            {selectedWidget.type === "line" ? (
              <div className="space-y-3">
                <div className="space-y-1.5">
                  <Label>Rotation (deg)</Label>
                  <Input
                    type="number"
                    min={-180}
                    max={180}
                    value={String(selectedWidget.props?.lineAngle ?? 0)}
                    onChange={(event) =>
                      updateWidgetProps(selectedWidget.id, {
                        lineAngle: clamp(Number.parseInt(event.target.value || "0", 10) || 0, -180, 180),
                      })
                    }
                  />
                </div>
                <div className="grid grid-cols-4 gap-2">
                  {[0, 45, 90, 135].map((angle) => (
                    <Button
                      key={`line-angle-${angle}`}
                      type="button"
                      size="sm"
                      variant={Number(selectedWidget.props?.lineAngle ?? 0) === angle ? "default" : "outline"}
                      onClick={() => updateWidgetProps(selectedWidget.id, { lineAngle: angle })}
                    >
                      {angle}Â°
                    </Button>
                  ))}
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1.5">
                    <Label>Thickness</Label>
                    <Input
                      type="number"
                      min={1}
                      max={24}
                      value={String(selectedWidget.props?.lineThickness ?? 2)}
                      onChange={(event) =>
                        updateWidgetProps(selectedWidget.id, {
                          lineThickness: clamp(Number.parseInt(event.target.value || "2", 10) || 2, 1, 24),
                        })
                      }
                    />
                  </div>
                  <div className="space-y-1.5">
                    <Label>Line Style</Label>
                    <Select
                      value={String(selectedWidget.props?.lineStyle || "solid")}
                      onValueChange={(value: BorderLineStyle) =>
                        updateWidgetProps(selectedWidget.id, { lineStyle: value })
                      }
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="solid">Solid</SelectItem>
                        <SelectItem value="dashed">Dashed</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div className="space-y-1.5">
                  <Label>Line Color</Label>
                  <Input
                    type="color"
                    value={String(selectedWidget.props?.lineColor || "#334155")}
                    onChange={(event) => updateWidgetProps(selectedWidget.id, { lineColor: event.target.value })}
                  />
                </div>
              </div>
            ) : null}

            {selectedWidget.type === "rectangle" ? (
              <div className="space-y-3">
                <div className="space-y-1.5">
                  <Label>Fill Color</Label>
                  <Input
                    type="color"
                    value={
                      /^#([0-9a-f]{3}|[0-9a-f]{6})$/i.test(String(selectedWidget.props?.fillColor || ""))
                        ? String(selectedWidget.props?.fillColor)
                        : "#ffffff"
                    }
                    onChange={(event) => updateWidgetProps(selectedWidget.id, { fillColor: event.target.value })}
                  />
                </div>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => updateWidgetProps(selectedWidget.id, { fillColor: "transparent" })}
                >
                  Transparent Fill
                </Button>
              </div>
            ) : null}
              </div>
            </details>

            <details className="rounded-md border bg-white p-2">
              <summary className="cursor-pointer select-none text-sm font-medium text-gray-700">Actions</summary>
              <div className="mt-2 grid grid-cols-2 gap-2">
              <Button
                variant="outline"
                onClick={() => duplicateWidget(selectedWidget.id)}
                className="gap-2"
              >
                <Copy className="h-4 w-4" />
                Duplicate
              </Button>
              <Button
                variant="outline"
                onClick={() => bringWidgetToFront(selectedWidget.id)}
                className="gap-2"
              >
                <LayoutPanelLeft className="h-4 w-4" />
                Bring Front
              </Button>
              <Button
                variant="destructive"
                onClick={() => removeWidget(selectedWidget.id)}
                className="gap-2"
              >
                <Trash2 className="h-4 w-4" />
                Delete
              </Button>
              </div>
            </details>
          </div>
        )}
      </CardContent>
    </Card>
  )
}

